import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, Building2, CalendarRange, CloudDownload, FileAudio, FileSpreadsheet, Layers, LayoutGrid, Loader2, PauseCircle, RotateCcw, Search, Sparkles, User,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  TlRecordingAudioModal,
  TlRecordingUrlActions,
  buildReportRecordingInfo,
} from "@/components/reports/tlRecordingAudio";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import SearchableAgentSelect from "@/components/common/SearchableAgentSelect";
import { BreakAnalysisRangeOverviewPanel } from "@/components/reports/BreakAnalysisRangeOverview";
import {
  PanelTableBody, PanelTableCell, PanelTableEmpty, PanelTableHead, PanelTableHeadRow, PanelTableRow, TableHeader,
} from "@/components/ui/panel-table";
import { formatReportDate, formatOnayLabel, TL_TABLE_COLUMNS } from "@/utils/tlRecordingReportHelpers";
import {
  agentsForSearchSelect,
  formatWeekRangeLabel,
  getReportColumns,
  truncateReportText,
} from "@/utils/managerReportHelpers";
import { getCurrentIsoWeekContext, resolveIsoYearForRow } from "@/utils/isoWeek";
import {
  formatTalkTime,
  getLastMonthRange,
  getLastWeekRange,
  getTodayStr,
  getYesterdayStr,
} from "@/utils/helpers";
import ReportTextDetailModal from "@/components/reports/ReportTextDetailModal";
import {
  KALITELI_DATA_META_FIELDS,
  KALITELI_DATA_TAB_ROWS,
  formatKaliteliRate,
  parseKaliteliDataWorkbook,
} from "@/utils/kaliteliDataExcelImport";
import KaliteliDataImportModal from "@/components/reports/KaliteliDataImportModal";
import SfDatamSyncResultModal from "@/components/reports/SfDatamSyncResultModal";
import SpecialAgentOverviewV2 from "@/components/reports/SpecialAgentOverviewV2";
import "./special-agent-report.css";
import "./special-agent-report-v2.css";

const TL_OVERVIEW_TEXT_FIELDS = [
  { key: "leader_report", label: "Tk Lideri Ses Kaydı Raporu" },
];

const STORAGE_KEY = "specialAgentReportState_v1";

const SAR_DATE_PRESETS = [
  { id: "today", label: "Bugün" },
  { id: "yesterday", label: "Dün" },
  { id: "lastWeek", label: "Geçen hafta" },
  { id: "lastMonth", label: "Geçen ay" },
  { id: "custom", label: "Tarih aralığı" },
];

const resolveSarDatePreset = (presetId) => {
  switch (presetId) {
    case "today": {
      const d = getTodayStr();
      return { start: d, end: d };
    }
    case "yesterday": {
      const d = getYesterdayStr();
      return { start: d, end: d };
    }
    case "lastWeek":
      return getLastWeekRange();
    case "lastMonth":
      return getLastMonthRange();
    default:
      return null;
  }
};

const loadSavedReportState = (storageKey = STORAGE_KEY) => {
  try {
    const raw = localStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const TL_TEXT_FIELDS = [
  { key: "assistant_report", label: "Asistan Ses Kaydı Raporu" },
  { key: "leader_report", label: "Tk Lideri Ses Kaydı Raporu" },
];

const KALITE_TEXT_FIELDS = [
  { key: "call1_evaluation", label: "1. Görüşme Değerlendirme" },
  { key: "call2_evaluation", label: "2. Görüşme Değerlendirme" },
];

const QUALITY_YESNO_OVERVIEW_FIELDS = [
  { key: "ordeleme", label: "İrdeleme" },
  { key: "mr_isteme", label: "MR İsteme" },
  { key: "mr_tepinme", label: "MR Üstünde Tepinme" },
  { key: "tedirginlik", label: "Tedirginlik" },
  { key: "uzmanlik", label: "Uzmanlık" },
];

const buildQualityYesNoOverviewMetrics = (row) => {
  if (!row) return [];
  return QUALITY_YESNO_OVERVIEW_FIELDS.map(({ key, label }) => ({
    label,
    value: formatOnayLabel(row[key]) || "—",
  }));
};

const normalizeYesNoCompare = (value) => {
  if (value === true || value === 1 || value === "1") return "evet";
  if (value === false || value === 0 || value === "0") return "hayir";
  const v = String(value ?? "")
    .toLocaleLowerCase("tr-TR")
    .trim()
    .replace(/\s+/g, " ");
  if (!v || v === "—" || v === "-" || v === "seçiniz" || v === "seciniz") return null;
  if (v === "evet" || v === "yes" || v === "true" || v === "e") return "evet";
  if (v === "hayir" || v === "hayır" || v === "no" || v === "false" || v === "h") return "hayir";
  return null;
};

const normalizeReportDateKey = (value) => {
  if (!value) return "";
  return String(value).trim().slice(0, 10);
};

/** Özet kartlarıyla aynı kayıt çifti: önce aynı tarih, yoksa en güncel TL + en güncel kalite. */
const pickTlKaliteCompareRows = (bundle) => {
  const tlItems = bundle?.tl_recordings?.items || [];
  const kaliteItems = bundle?.kalite_degerlendirme?.items || [];
  if (!tlItems.length || !kaliteItems.length) {
    return { tlRow: null, kaliteRow: null };
  }
  const kaliteByDate = new Map(
    kaliteItems.map((row) => [normalizeReportDateKey(row.report_date), row]),
  );
  for (const tlRow of tlItems) {
    const key = normalizeReportDateKey(tlRow.report_date);
    if (key && kaliteByDate.has(key)) {
      return { tlRow, kaliteRow: kaliteByDate.get(key) };
    }
  }
  return { tlRow: tlItems[0], kaliteRow: kaliteItems[0] };
};

/** TL ses kaydı vs kalite değerlendirme — 5 kriter uyumu. */
const buildTlKaliteAlignmentSummary = (bundle) => {
  const tlItems = bundle?.tl_recordings?.items || [];
  const kaliteItems = bundle?.kalite_degerlendirme?.items || [];
  const tlTotal = Number(bundle?.tl_recordings?.total ?? tlItems.length);
  const kaliteTotal = Number(bundle?.kalite_degerlendirme?.total ?? kaliteItems.length);
  if (tlTotal <= 0 || kaliteTotal <= 0) return null;

  const { tlRow, kaliteRow } = pickTlKaliteCompareRows(bundle);
  if (!tlRow || !kaliteRow) {
    return {
      percent: null,
      matched: 0,
      compared: 0,
      diffs: [],
      message:
        "Tk. Lideri ve Kalite kayıtları var; detay satırları yüklenmedi. Lütfen Sorgula ile raporu yenileyin.",
    };
  }

  const diffs = [];
  let matched = 0;
  let compared = 0;

  QUALITY_YESNO_OVERVIEW_FIELDS.forEach(({ key, label }) => {
    const tlVal = normalizeYesNoCompare(tlRow[key]);
    const kaliteVal = normalizeYesNoCompare(kaliteRow[key]);
    if (tlVal === null || kaliteVal === null) return;
    compared += 1;
    if (tlVal === kaliteVal) matched += 1;
    else diffs.push(label);
  });

  if (compared === 0) {
    return {
      percent: null,
      matched: 0,
      compared: 0,
      diffs: [],
      message:
        "Tk. Lideri ve Kalite değerlendirme kayıtları var; 5 kriterde her iki tarafta da Evet/Hayır seçimi olmadığı için uyum yüzdesi hesaplanamadı.",
    };
  }

  const percent = Math.round((matched / compared) * 100);
  let diffSentence = "";
  if (diffs.length === 0) {
    diffSentence = compared === QUALITY_YESNO_OVERVIEW_FIELDS.length
      ? "Tüm kriterlerde görüş birliği vardır."
      : `${compared} karşılaştırılan kriterde görüş birliği vardır.`;
  } else if (diffs.length === 1) {
    diffSentence = `${diffs[0]} konusunda farklılık vardır.`;
  } else {
    diffSentence = `${diffs.slice(0, -1).join(", ")} ve ${diffs[diffs.length - 1]} konularında farklılık vardır.`;
  }

  const message = diffs.length > 0
    ? `Tk. Lideri ve Kalite değerlendirme raporu %${percent} oranında birbiri ile uyumludur; ${diffSentence}`
    : `Tk. Lideri ve Kalite değerlendirme raporu %${percent} oranında birbiri ile uyumludur. ${diffSentence}`;

  return { percent, matched, compared, diffs, message };
};

const SPECIAL_AGENT_OVERVIEW_ORDER = ["mola", "mr", "kaliteli", "tl", "tl_yeni", "kalite"];

const TL_YENI_TEXT_FIELDS = [
  { key: "deficiencies_action", label: "2. Gün — Eksiklikler / Aksiyon planı" },
  { key: "development_report", label: "4. Gün — Genel Rapor — Gelişim gösterdi mi?" },
  { key: "decision_text", label: "5. Gün — KARAR" },
];

/** Süper admin için varsayılan firma: SPN (kod veya ad) */
const resolveSpnCompanyId = (companies) => {
  const list = companies || [];
  const byKod = list.find((c) => String(c.kod ?? "").trim().toLowerCase() === "spn");
  if (byKod?.id) return String(byKod.id);
  const byAd = list.find((c) => {
    const ad = String(c.ad ?? c.name ?? "").trim().toUpperCase();
    return ad === "SPN" || ad.startsWith("SPN ");
  });
  if (byAd?.id) return String(byAd.id);
  return "";
};

const countYesNoField = (items, key) => {
  const out = { evet: 0, hayir: 0, bos: 0 };
  (items || []).forEach((row) => {
    const v = String(row?.[key] ?? "").toLowerCase().trim();
    if (v === "evet") out.evet += 1;
    else if (v === "hayir" || v === "hayır") out.hayir += 1;
    else out.bos += 1;
  });
  return out;
};

const countApprovalField = (items, key) => countYesNoField(items, key);

/** Sekmelere girmeden alan alan özet metrikleri */
const SF_DATAM_RATIO_LABELS = {
  mr_isteme: "MR isteme oranı",
  mr_alma: "MR alma oranı",
  randevu: "Randevu alma oranı",
  islem: "İşlem oranı",
};

const SF_DATAM_COUNT_LABELS = {
  mr_isteme: "MR istenen data sayısı",
  mr_alma: "Toplam alınan MR sayısı",
  randevu: "Toplam alınan randevu sayısı",
  islem: "Toplam işlem olan hasta sayısı",
};

const SF_DATAM_RATIO_ORDER = ["mr_isteme", "mr_alma", "randevu", "islem"];

const formatSfDatamCount = (n) => (n != null && n !== "" ? String(n) : "—");

const appendSfDatamRatioMetrics = (metrics, ratios) => {
  const byKey = Object.fromEntries((ratios || []).map((r) => [r.key, r]));
  SF_DATAM_RATIO_ORDER.forEach((key) => {
    const r = byKey[key];
    if (!r) return;
    if (key !== "mr_isteme" && r.numerator != null) {
      metrics.push({
        label: SF_DATAM_COUNT_LABELS[key],
        value: formatSfDatamCount(r.numerator),
      });
    }
    metrics.push({
      label: SF_DATAM_RATIO_LABELS[key] || r.label,
      value: r.display ?? "—",
    });
  });
};

/** Özet + MR sekmesi: toplam data + adetler + oranlar (API overview_metrics eski olsa bile). */
const buildSfDatamOverviewMetrics = (mr) => {
  if (!mr?.has_agent_row) return [];
  const metrics = [
    {
      label: "Toplam alınan data",
      value: mr.column_total != null && mr.column_total !== "" ? String(mr.column_total) : "—",
    },
  ];
  const ratios = mr.ratio_metrics || [];
  if (ratios.length > 0) {
    const mrIsteme = ratios.find((r) => r.key === "mr_isteme");
    if (mrIsteme?.numerator != null) {
      metrics.push({
        label: SF_DATAM_COUNT_LABELS.mr_isteme,
        value: formatSfDatamCount(mrIsteme.numerator),
      });
    }
    appendSfDatamRatioMetrics(metrics, ratios);
    return metrics;
  }
  const byLabel = Object.fromEntries((mr.overview_metrics || []).map((m) => [m.label, m.value]));
  if (byLabel["Toplam kayıt (SF)"] != null && metrics[0].value === "—") {
    metrics[0].value = String(byLabel["Toplam kayıt (SF)"]);
  }
  ["MR isteme oranı", "MR alma oranı"].forEach((label) => {
    if (byLabel[label] != null) metrics.push({ label, value: byLabel[label] });
  });
  const randevuVal = byLabel["Randevu alma oranı"] ?? byLabel["Randevu oranı"];
  if (randevuVal != null) {
    metrics.push({ label: "Randevu alma oranı", value: randevuVal });
  }
  if (byLabel["İşlem oranı"] != null) {
    metrics.push({ label: "İşlem oranı", value: byLabel["İşlem oranı"] });
  }
  return metrics;
};

const formatKaliteliCount = (v) => (v != null && v !== "" ? String(v) : "—");

/** Genel özet kartı — API overview_metrics eski olsa bile satırdan tamamlar. */
const buildKaliteliOverviewMetrics = (kaliteli) => {
  const row = kaliteli?.row;
  if (!row) return kaliteli?.overview_metrics || [];
  const fromApi = kaliteli.overview_metrics || [];
  if (fromApi.some((m) => m.label === "WP işlem sayısı")) {
    return fromApi;
  }
  return [
    { label: "WP data sayısı", value: formatKaliteliCount(row.wp_total_data) },
    { label: "WP işlem sayısı", value: formatKaliteliCount(row.wp_processed_count) },
    { label: "WP işlem oranı", value: formatKaliteliRate(row.wp_conversion_rate) },
    { label: "Kaliteli data sayısı", value: formatKaliteliCount(row.kaliteli_assigned_data) },
    { label: "Kaliteli data işlem sayısı", value: formatKaliteliCount(row.kaliteli_processed_count) },
    { label: "Kaliteli işlem oranı", value: formatKaliteliRate(row.kaliteli_process_rate) },
    { label: "Alınan dış arama", value: formatKaliteliCount(row.dis_arama_alinan) },
    { label: "Planlanan dış arama", value: formatKaliteliCount(row.dis_arama_planlanan) },
  ];
};

const AGENT_MIN_WORKING_DAY_SECONDS = 3600;

const getMolaDayTotalTalkSeconds = (day) =>
  (Number(day?.total_talk_seconds) || 0) + (Number(day?.off_shift_talk_seconds) || 0);

/** Konuşması ≥1 saat olan günler — ortalama konuşma/mola (aktif gün değil). */
const computeMolaWorkingDayAverages = (summary, dailyBreakdown) => {
  const minSec = Number(summary?.min_working_day_seconds) || AGENT_MIN_WORKING_DAY_SECONDS;
  const wdFromApi = summary?.working_days ?? summary?.high_talk_days;
  if (wdFromApi != null && summary?.avg_daily_talk_seconds != null) {
    const workingDays = Number(wdFromApi) || 0;
    return {
      workingDays,
      avgTalkSec: workingDays > 0 ? Number(summary.avg_daily_talk_seconds) : 0,
      avgBreakSec: workingDays > 0
        ? Number(summary.avg_daily_break_seconds ?? summary.avg_high_talk_break_seconds ?? 0)
        : 0,
    };
  }
  const days = (dailyBreakdown || []).filter(
    (d) => d?.has_activity && getMolaDayTotalTalkSeconds(d) >= minSec,
  );
  const workingDays = days.length;
  if (workingDays === 0) {
    return { workingDays: 0, avgTalkSec: 0, avgBreakSec: 0 };
  }
  const totalTalk = days.reduce((s, d) => s + getMolaDayTotalTalkSeconds(d), 0);
  const totalBreak = days.reduce((s, d) => s + (Number(d.between_break_seconds) || 0), 0);
  return {
    workingDays,
    avgTalkSec: Math.round(totalTalk / workingDays),
    avgBreakSec: Math.round(totalBreak / workingDays),
  };
};

const buildSpecialAgentOverview = (bundle, isDark) => {
  if (!bundle) return [];

  const tlItems = bundle.tl_recordings?.items || [];
  const tlTotal = bundle.tl_recordings?.total ?? tlItems.length;
  const ask = countApprovalField(tlItems, "ask_onay");
  const tksk = countApprovalField(tlItems, "tksk_onay");
  const kaliteItems = bundle.kalite_degerlendirme?.items || [];
  const kaliteTotal = bundle.kalite_degerlendirme?.total ?? kaliteItems.length;
  const { tlRow: compareTlRow, kaliteRow: compareKaliteRow } = pickTlKaliteCompareRows(bundle);
  const latestTl = compareTlRow || tlItems[0];
  const latestKalite = compareKaliteRow || kaliteItems[0];

  const molaSummary = bundle.mola_report?.summary || {};
  const molaDays = bundle.mola_report?.daily_breakdown || [];
  const periodDays = molaDays.length;
  const molaWorking = computeMolaWorkingDayAverages(molaSummary, molaDays);

  const tlYeniItems = bundle.tl_yeni_asistan?.items || [];
  const tlYeniTotal = bundle.tl_yeni_asistan?.total ?? tlYeniItems.length;
  const latestYeni = tlYeniItems[0];

  const buildTextBlocks = (row, fields, { includeEmpty = false } = {}) => fields
    .map((f) => ({
      label: f.label,
      value: String(row?.[f.key] || "").trim() || (includeEmpty ? "—" : ""),
    }))
    .filter((b) => includeEmpty || b.value);

  const overviewSections = {
    tl: {
      tabId: "tl",
      title: "Tk. Liderleri Ses Kayıtları",
      accent: isDark ? "from-violet-500/20 to-purple-500/5 border-violet-500/25" : "from-violet-50 to-white border-violet-200",
      icon: FileAudio,
      empty: tlTotal === 0,
      metrics: tlTotal === 0
        ? [{ label: "Kayıt", value: "Yok" }]
        : [
            { label: "ASK onay", value: `Evet ${ask.evet} · Hayır ${ask.hayir}` },
            { label: "TKSK onay", value: `Evet ${tksk.evet} · Hayır ${tksk.hayir}` },
            ...buildQualityYesNoOverviewMetrics(latestTl),
          ],
      textBlocks: latestTl ? buildTextBlocks(latestTl, TL_OVERVIEW_TEXT_FIELDS, { includeEmpty: true }) : [],
      textLayout: "stack",
    },
    kalite: {
      tabId: "kalite",
      title: "Kalite Değerlendirme",
      accent: isDark ? "from-blue-500/20 to-cyan-500/5 border-blue-500/25" : "from-blue-50 to-white border-blue-200",
      icon: FileSpreadsheet,
      empty: kaliteTotal === 0,
      metrics: kaliteTotal === 0
        ? [{ label: "Kayıt", value: "Yok" }]
        : [
            { label: "Değerlendirme", value: String(kaliteTotal) },
            ...(latestKalite?.quality_user_name ? [{ label: "Son kalite", value: latestKalite.quality_user_name }] : []),
            ...buildQualityYesNoOverviewMetrics(latestKalite),
          ],
      textBlocks: latestKalite ? buildTextBlocks(latestKalite, KALITE_TEXT_FIELDS, { includeEmpty: true }) : [],
      textLayout: "split2",
    },
    mola: {
      tabId: "mola",
      title: "Mola Raporu",
      accent: isDark ? "from-amber-500/20 to-orange-500/5 border-amber-500/25" : "from-amber-50 to-white border-amber-200",
      icon: PauseCircle,
      empty: periodDays === 0 && !(molaSummary.active_days > 0),
      metrics: [
        { label: "Çalışma günü", value: String(molaWorking.workingDays) },
        {
          label: "Ortalama günlük konuşma",
          value: molaWorking.workingDays > 0 ? formatTalkTime(molaWorking.avgTalkSec) : "—",
        },
        {
          label: "Ortalama günlük mola",
          value: molaWorking.workingDays > 0 ? formatTalkTime(molaWorking.avgBreakSec) : "—",
        },
      ],
      insight: periodDays > 0
        ? `${periodDays} günlük özel dönem analizi — grafik ve metrik tablosu sekmede.`
        : "Bu aralıkta günlük analiz verisi yok.",
    },
    tl_yeni: {
      tabId: "tl_yeni",
      title: "Takım Lideri Yeni Asistan",
      accent: isDark ? "from-emerald-500/20 to-teal-500/5 border-emerald-500/25" : "from-emerald-50 to-white border-emerald-200",
      icon: Layers,
      empty: tlYeniTotal === 0,
      metrics: tlYeniTotal === 0
        ? [{ label: "Kayıt", value: "Yok" }]
        : [
            { label: "Form kaydı", value: String(tlYeniTotal) },
            ...(latestYeni?.leader_name ? [{ label: "Son TL", value: latestYeni.leader_name }] : []),
            ...(latestYeni?.report_date ? [{ label: "Son tarih", value: formatReportDate(latestYeni.report_date) }] : []),
          ],
      textBlocks: latestYeni ? buildTextBlocks(latestYeni, TL_YENI_TEXT_FIELDS, { includeEmpty: true }) : [],
      textLayout: "split3",
    },
    kaliteli: (() => {
      const kaliteli = bundle.kaliteli_data_analiz || {};
      const batches = kaliteli.batches || [];
      const hasRow = !!kaliteli.row;
      const selectedBatch = batches.find((b) => b.id === kaliteli.selected_batch_id);
      return {
        tabId: "kaliteli",
        title: "Kaliteli Data Analiz",
        accent: isDark ? "from-teal-500/20 to-cyan-500/5 border-teal-500/25" : "from-teal-50 to-white border-teal-200",
        icon: Sparkles,
        empty: batches.length === 0,
        placeholder: false,
        metrics: hasRow
          ? buildKaliteliOverviewMetrics(kaliteli)
          : (batches.length > 0
            ? [{ label: "Kayıt", value: "Bu asistan için seçili sheet'te satır yok" }]
            : [{ label: "Kayıt", value: "Import yok" }]),
        batchOptions: batches,
        selectedBatchId: kaliteli.selected_batch_id ?? null,
        batchLabel: selectedBatch?.sheet_title || null,
        insight: selectedBatch
          ? `${selectedBatch.sheet_title} · ${formatReportDate(selectedBatch.period_start)} – ${formatReportDate(selectedBatch.period_end)}`
          : null,
      };
    })(),
    mr: (() => {
      const mr = bundle.toplam_data_mr || {};
      const hasRow = !!mr.has_agent_row;
      const synced = !!mr.synced;
      return {
        tabId: "mr",
        title: "Toplam Data MR",
        accent: isDark ? "from-sky-500/15 to-slate-800/30 border-sky-600/30" : "from-sky-50 to-white border-sky-200",
        icon: CloudDownload,
        empty: !hasRow,
        placeholder: false,
        metrics: hasRow
          ? buildSfDatamOverviewMetrics(mr)
          : [{ label: "Kayıt", value: synced ? "Bu asistan için SF satırı yok" : "SF senkron yapılmadı" }],
        insight: mr.batch
          ? `Salesforce · ${formatReportDate(mr.batch.start_date)} – ${formatReportDate(mr.batch.end_date)}`
          : "Tarih seçip SF ile veriyi çekin",
      };
    })(),
  };

  return SPECIAL_AGENT_OVERVIEW_ORDER.map((id) => overviewSections[id]);
};

const OverviewMetric = ({ label, value, isDark, labelClass }) => (
  <div className="flex justify-between gap-3 text-xs">
    <span className={labelClass}>{label}</span>
    <span className={`font-medium text-right ${isDark ? "text-slate-200" : "text-gray-900"}`}>{value}</span>
  </div>
);

const OverviewTextBlock = ({ label, value, isDark, textClass, layoutClass }) => (
  <div className={layoutClass}>
    <p className={`text-[10px] font-semibold mb-1 ${isDark ? "text-slate-400" : "text-gray-500"}`}>{label}</p>
    <p className={`text-[11px] leading-relaxed whitespace-pre-wrap break-words ${textClass}`}>{value}</p>
  </div>
);

const SpecialAgentReportOverview = ({
  apiBase, bundle, isDark, textClass, labelClass, onOpenTab, onKaliteliBatchChange, variant = "v1",
}) => {
  const sections = useMemo(() => buildSpecialAgentOverview(bundle, isDark), [bundle, isDark]);
  const tlKaliteAlignment = useMemo(() => buildTlKaliteAlignmentSummary(bundle), [bundle]);
  const isV2 = variant === "v2";

  const kaliteliSection = sections.find((s) => s.tabId === "kaliteli");
  const kaliteliBatchSelect = kaliteliSection?.batchOptions?.length > 0 ? (
    <div className="mb-2" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
      <Select
        value={kaliteliSection.selectedBatchId != null ? String(kaliteliSection.selectedBatchId) : undefined}
        onValueChange={(v) => onKaliteliBatchChange?.(parseInt(v, 10))}
      >
        <SelectTrigger className={`h-8 text-xs ${isDark ? "bg-slate-900 border-slate-700" : ""}`}>
          <SelectValue placeholder="Sheet dönemi seçin" />
        </SelectTrigger>
        <SelectContent>
          {kaliteliSection.batchOptions.map((b) => (
            <SelectItem key={b.id} value={String(b.id)}>
              {b.sheet_title}
              {" · "}
              {formatReportDate(b.period_start)} – {formatReportDate(b.period_end)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  ) : null;

  if (isV2) {
    return (
      <SpecialAgentOverviewV2
        apiBase={apiBase}
        sections={sections}
        bundle={bundle}
        tlKaliteAlignment={tlKaliteAlignment}
        isDark={isDark}
        textClass={textClass}
        labelClass={labelClass}
        onOpenTab={onOpenTab}
        kaliteliBatchSelect={kaliteliBatchSelect}
      />
    );
  }

  return (
    <div className="space-y-4 sar-tab-enter">
      <p className={`text-xs ${labelClass}`}>
        Aşağıdaki kartlar 6 rapor alanının özetidir. Detay için karta tıklayın veya üst sekmeleri kullanın.
      </p>
      {tlKaliteAlignment ? (
        <div
          className={`rounded-xl border px-3 py-2.5 text-xs leading-relaxed w-full ${
            isDark ? "border-violet-500/35 bg-violet-500/10 text-violet-100" : "border-violet-200 bg-violet-50 text-violet-950"
          }`}
        >
          <p className={`font-semibold mb-0.5 ${isDark ? "text-violet-200" : "text-violet-800"}`}>
            TL · Kalite uyumu
            {tlKaliteAlignment.percent != null ? (
              <span className={`ml-2 font-bold tabular-nums ${isDark ? "text-white" : "text-gray-900"}`}>
                %{tlKaliteAlignment.percent}
              </span>
            ) : null}
          </p>
          <p>{tlKaliteAlignment.message}</p>
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {sections.map((section, sectionIndex) => {
          const Icon = section.icon;
          const accent = isV2 ? "" : section.accent;
          return (
            <button
              key={section.tabId}
              type="button"
              onClick={() => !section.placeholder && onOpenTab(section.tabId)}
              disabled={section.placeholder}
              style={isV2 ? { animationDelay: `${sectionIndex * 90}ms` } : undefined}
              className={`sar-overview-card text-left rounded-xl border bg-gradient-to-br p-4 ${accent} ${
                isV2 ? "sar-v2-overview-card" : ""
              } ${
                section.placeholder
                  ? "opacity-75 cursor-default"
                  : (isV2 ? "" : (isDark ? "hover:border-teal-500/40 hover:shadow-lg hover:shadow-black/20" : "hover:border-teal-300 hover:shadow-md"))
              }`}
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex items-center gap-2 min-w-0">
                  <div className={`p-1.5 rounded-lg shrink-0 ${isDark ? "bg-slate-800/80" : "bg-white/90"}`}>
                    <Icon className={`w-4 h-4 ${isDark ? "text-teal-400" : "text-teal-600"}`} />
                  </div>
                  <h4 className={`text-sm font-semibold leading-snug ${textClass}`}>{section.title}</h4>
                </div>
                {!section.placeholder && (
                  <span className={`inline-flex items-center text-[10px] font-medium shrink-0 ${isDark ? "text-teal-400" : "text-teal-700"}`}>
                    Detay <ArrowRight className="w-3 h-3 ml-0.5" />
                  </span>
                )}
              </div>

              {section.tabId === "kaliteli" && section.batchOptions?.length > 0 && (
                <div className="mb-2" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
                  <Select
                    value={section.selectedBatchId != null ? String(section.selectedBatchId) : undefined}
                    onValueChange={(v) => onKaliteliBatchChange?.(parseInt(v, 10))}
                  >
                    <SelectTrigger className={`h-8 text-xs ${isDark ? "bg-slate-900 border-slate-700" : ""}`}>
                      <SelectValue placeholder="Sheet dönemi seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {section.batchOptions.map((b) => (
                        <SelectItem key={b.id} value={String(b.id)}>
                          {b.sheet_title}
                          {" · "}
                          {formatReportDate(b.period_start)} – {formatReportDate(b.period_end)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-1.5 mb-3">
                {section.metrics.map((m) => (
                  <OverviewMetric key={`${section.tabId}-${m.label}`} {...m} isDark={isDark} labelClass={labelClass} />
                ))}
              </div>

              {section.textBlocks?.length > 0 && (
                <div
                  className={`border-t pt-2 space-y-2 ${
                    section.textLayout === "split2"
                      ? "grid grid-cols-1 sm:grid-cols-2 gap-3"
                      : section.textLayout === "split3"
                        ? "grid grid-cols-1 sm:grid-cols-3 gap-3"
                        : ""
                  } ${isDark ? "border-slate-700/80" : "border-gray-200/80"}`}
                >
                  {section.textBlocks.map((block) => (
                    <OverviewTextBlock
                      key={`${section.tabId}-${block.label}`}
                      label={block.label}
                      value={block.value}
                      isDark={isDark}
                      textClass={textClass}
                      layoutClass={
                        section.textLayout === "stack"
                          ? `rounded-md p-2 ${isDark ? "bg-slate-900/50" : "bg-white/80"}`
                          : `rounded-md p-2 min-h-[4rem] ${isDark ? "bg-slate-900/50" : "bg-white/80"}`
                      }
                    />
                  ))}
                </div>
              )}

              {section.insight && (
                <p className={`text-[11px] leading-relaxed border-t pt-2 ${isDark ? "border-slate-700/80 text-slate-400" : "border-gray-200/80 text-gray-600"}`}>
                  {section.insight}
                </p>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const LOADING_STEPS = [
  { key: "mola", label: "Mola raporu" },
  { key: "mr", label: "Toplam data MR" },
  { key: "kaliteli", label: "Kaliteli data analiz" },
  { key: "tl", label: "Tk. Liderleri ses kayıtları" },
  { key: "tl_yeni", label: "Yeni asistan raporu" },
  { key: "kalite", label: "Kalite değerlendirme" },
];

const TAB_DEFS = [
  { id: "overview", label: "Genel Özet", icon: LayoutGrid },
  { id: "mola", label: "Mola Raporu", icon: PauseCircle },
  { id: "mr", label: "Toplam Data MR", icon: CloudDownload },
  { id: "kaliteli", label: "Kaliteli Data", icon: Sparkles },
  { id: "tl", label: "Ses Kayıtları", icon: FileAudio },
  { id: "tl_yeni", label: "Yeni Asistan", icon: Layers },
  { id: "kalite", label: "Kalite", icon: FileSpreadsheet },
];

const renderClickableReportText = (value, row, fieldKey, onOpenTextDetail, isDark, labelClass) => {
  const text = String(value || "").trim();
  if (!text) return <span className={`text-[11px] ${labelClass}`}>—</span>;
  return (
    <button
      type="button"
      onClick={() => onOpenTextDetail(row, fieldKey)}
      className={`text-left w-full rounded-md line-clamp-3 leading-relaxed transition-colors px-2 py-1.5 -mx-2 text-[12px] ${
        isDark ? "hover:bg-slate-800/60 text-slate-200" : "hover:bg-violet-50 text-gray-700"
      }`}
      title="Tam metni görüntüle"
    >
      {truncateReportText(text, 120)}
    </button>
  );
};

const formatMgrCell = (reportType, col, row, onOpenTextDetail, isDark, labelClass) => {
  const value = row?.[col.key];
  if (col.type === "yesno") return formatOnayLabel(value) || "—";
  if (col.type === "leader_select") return row?.leader_name || "—";
  if (col.type === "quality_select") return row?.quality_user_name || "—";
  if (col.type === "agent_select") return row?.assistant_name || "—";
  if (col.type === "week_display") {
    if (!row?.week_number && !row?.report_date) return "—";
    const isoYear = resolveIsoYearForRow(row, getCurrentIsoWeekContext().isoYear);
    return formatWeekRangeLabel(isoYear, row?.week_number);
  }
  if (col.type === "date" || col.key === "yeni_dataya_gecis_tarihi") return formatReportDate(value);
  if (col.type === "recording_url") return value ? truncateReportText(value, 48) : "—";
  if (col.type === "text") {
    if (onOpenTextDetail) return renderClickableReportText(value, row, col.key, onOpenTextDetail, isDark, labelClass);
    return truncateReportText(value, 120);
  }
  if (col.type === "readonly") return value ?? "—";
  return value ?? "—";
};

const ReadOnlyReportTable = ({ columns, rows, formatCell, isDark, emptyLabel }) => {
  if (!rows?.length) {
    return <PanelTableEmpty colSpan={columns.length}>{emptyLabel}</PanelTableEmpty>;
  }
  return rows.map((row) => (
    <PanelTableRow key={row.id || `${row.report_date}-${row.week_number}`}>
      {columns.map((col) => (
        <PanelTableCell key={col.key} className="align-top text-xs max-w-[280px]">
          {formatCell(col, row)}
        </PanelTableCell>
      ))}
    </PanelTableRow>
  ));
};

const KaliteliDataDetailPanel = ({
  bundle, isDark, textClass, labelClass, kaliteliBatchId, onBatchChange, onRefetch,
}) => {
  const kaliteli = bundle?.kaliteli_data_analiz || {};
  const batches = kaliteli.batches || [];
  const row = kaliteli.row;

  if (batches.length === 0) {
    return (
      <div className={`rounded-xl border border-dashed p-10 text-center ${isDark ? "border-slate-700 bg-slate-900/30" : "border-gray-200 bg-gray-50/80"}`}>
        <Sparkles className={`w-10 h-10 mx-auto mb-3 ${isDark ? "text-teal-400" : "text-teal-600"}`} />
        <p className={`text-sm ${labelClass}`}>Henüz Kaliteli Data importu yok. Sorgu satırındaki Excel Import ile yükleyin.</p>
        <p className={`text-xs mt-2 ${labelClass}`}>Import sonrası özet için önce Sorgula&apos;ya basın veya agent + tarih seçiliyken import edin.</p>
      </div>
    );
  }

  const formatCell = (key, value) => {
    if (key.includes("rate") || key.includes("oranı") || key.includes("oran")) {
      return formatKaliteliRate(value);
    }
    if (value === null || value === undefined || value === "") return "—";
    return String(value);
  };

  return (
    <div className="space-y-4">
      <div className={`rounded-lg border px-3 py-3 space-y-2 ${isDark ? "border-slate-800 bg-slate-900/40" : "border-gray-200 bg-white"}`}>
        <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between">
          <span className={`text-xs font-medium ${labelClass}`}>Sheet dönemi</span>
          <span className={`text-[10px] ${labelClass}`}>
            Rapor tarihinden bağımsız · varsayılan en son Excel importu
          </span>
        </div>
        <Select
          value={(kaliteliBatchId ?? kaliteli.selected_batch_id) != null
            ? String(kaliteliBatchId ?? kaliteli.selected_batch_id)
            : undefined}
          onValueChange={(v) => {
            const id = parseInt(v, 10);
            onBatchChange(id);
            onRefetch(id);
          }}
        >
          <SelectTrigger className={`h-9 text-sm max-w-md ${isDark ? "bg-slate-900 border-slate-700" : ""}`}>
            <SelectValue placeholder="Dönem seçin" />
          </SelectTrigger>
          <SelectContent>
            {batches.map((b) => (
              <SelectItem key={b.id} value={String(b.id)}>
                {b.sheet_title}
                {" · "}
                {formatReportDate(b.period_start)} – {formatReportDate(b.period_end)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!row ? (
        <div className={`rounded-lg border p-8 text-center text-sm ${isDark ? "border-slate-800 text-slate-400" : "border-gray-200 text-gray-600"}`}>
          Bu asistan için seçili sheet&apos;te satır yok (Excel isim eşleşmesini kontrol edin).
        </div>
      ) : (
        <div className={`rounded-xl border overflow-hidden ${isDark ? "border-slate-800" : "border-gray-200"}`}>
          <div className={`grid grid-cols-1 sm:grid-cols-3 gap-px ${isDark ? "bg-slate-800" : "bg-gray-200"}`}>
            {KALITELI_DATA_META_FIELDS.map((field) => (
              <div key={field.key} className={`p-3 ${isDark ? "bg-slate-950/60" : "bg-white"}`}>
                <p className={`text-[10px] font-semibold uppercase tracking-wide mb-1 ${labelClass}`}>{field.label}</p>
                <p className={`text-sm font-medium ${textClass}`}>{formatCell(field.key, row[field.key])}</p>
              </div>
            ))}
          </div>
          <div className={`flex flex-col gap-px mt-px ${isDark ? "bg-slate-800" : "bg-gray-200"}`}>
            {KALITELI_DATA_TAB_ROWS.map((rowDef) => (
              <div key={rowDef.fields.map((f) => f.key).join("-")} className={`${rowDef.rowClass} gap-px`}>
                {rowDef.fields.map((field) => (
                  <div
                    key={field.key}
                    className={`p-3 min-h-[4.25rem] ${isDark ? "bg-slate-950/60" : "bg-white"} ${
                      field.key === "total_assigned_data" ? "sm:py-4" : ""
                    }`}
                  >
                    <p className={`text-[10px] font-semibold uppercase tracking-wide mb-1 ${labelClass}`}>
                      {field.label}
                    </p>
                    <p className={`text-sm font-semibold ${field.key === "total_assigned_data" ? "text-base" : ""} ${textClass}`}>
                      {formatCell(field.key, row[field.key])}
                    </p>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const SfDatamMrPanel = ({ bundle, isDark, textClass, labelClass, variant = "v1" }) => {
  const isV2 = variant === "v2";
  const mrCardClass = isV2 ? "sar-v2-mr-card" : "";
  const mr = bundle?.toplam_data_mr;
  if (!mr?.synced) {
    return (
      <div className={`rounded-xl border border-dashed p-10 text-center ${isDark ? "border-slate-700 bg-slate-900/30" : "border-gray-200 bg-gray-50/80"}`}>
        <CloudDownload className={`w-10 h-10 mx-auto mb-3 ${isDark ? "text-sky-400" : "text-sky-600"}`} />
        <p className={`text-sm ${labelClass}`}>Bu tarih aralığı için SF senkronu yok. Üstteki <strong>SF</strong> ile Salesforce&apos;tan çekin.</p>
      </div>
    );
  }
  if (!mr.has_agent_row) {
    return (
      <div className={`rounded-lg border p-8 text-center text-sm ${isDark ? "border-slate-800 text-slate-400" : "border-gray-200 text-gray-600"}`}>
        Senkron var ancak seçili asistan SF raporunda eşleşmedi veya kayıt yok.
      </div>
    );
  }
  const stageOrder = Array.isArray(mr.stage_order) && mr.stage_order.length
    ? mr.stage_order
    : Object.keys(mr.by_stage || {});
  const stages = stageOrder.map((stage) => [stage, (mr.by_stage || {})[stage]]);
  const ratios = mr.ratio_metrics || [];
  const ratioByKey = Object.fromEntries(ratios.map((r) => [r.key, r]));
  const orderedRatios = SF_DATAM_RATIO_ORDER.map((key) => ratioByKey[key]).filter(Boolean);
  const mrIstemeRatio = ratioByKey.mr_isteme;
  const totalDisplay = mr.column_total != null && mr.column_total !== "" ? String(mr.column_total) : "—";
  const showSummaryCards = ratios.length > 0 || totalDisplay !== "—";

  return (
    <div className="space-y-4">
      {showSummaryCards && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          <div
            className={`rounded-xl border p-4 text-center ${mrCardClass} ${isDark ? "border-sky-800/60 bg-sky-950/40" : "border-sky-300 bg-sky-50"}`}
          >
            <p className={`text-[11px] font-semibold uppercase tracking-wide mb-1 ${labelClass}`}>Toplam alınan data</p>
            <p className={`text-2xl font-bold tabular-nums ${isDark ? "text-sky-100" : "text-sky-950"}`}>
              {totalDisplay}
            </p>
            {mrIstemeRatio?.numerator != null ? (
              <div className={`mt-3 pt-3 border-t ${isDark ? "border-sky-800/60" : "border-sky-200"}`}>
                <p className={`text-[10px] font-semibold uppercase tracking-wide mb-0.5 ${labelClass}`}>
                  {SF_DATAM_COUNT_LABELS.mr_isteme}
                </p>
                <p className={`text-lg font-bold tabular-nums ${isDark ? "text-sky-200" : "text-sky-900"}`}>
                  {formatSfDatamCount(mrIstemeRatio.numerator)}
                </p>
              </div>
            ) : null}
          </div>
          {orderedRatios.map((r) => (
            <div
              key={r.key}
              className={`rounded-xl border p-4 text-center ${mrCardClass} ${isDark ? "border-sky-800/60 bg-sky-950/30" : "border-sky-200 bg-sky-50/90"}`}
            >
              {r.key !== "mr_isteme" && r.numerator != null ? (
                <div className="mb-3">
                  <p className={`text-[10px] font-semibold uppercase tracking-wide mb-0.5 ${labelClass}`}>
                    {SF_DATAM_COUNT_LABELS[r.key]}
                  </p>
                  <p className={`text-lg font-bold tabular-nums ${isDark ? "text-cyan-100" : "text-sky-950"}`}>
                    {formatSfDatamCount(r.numerator)}
                  </p>
                </div>
              ) : null}
              <p className={`text-[11px] font-semibold uppercase tracking-wide mb-1 ${labelClass}`}>
                {SF_DATAM_RATIO_LABELS[r.key] || r.label}
              </p>
              <p className={`text-2xl font-bold tabular-nums ${isDark ? "text-sky-200" : "text-sky-900"}`}>
                {r.display ?? "—"}
              </p>
              {r.denominator != null && r.numerator != null ? (
                <p className={`text-[10px] mt-1 tabular-nums ${labelClass}`}>
                  {r.numerator} / {r.denominator}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      )}
      <div className={`rounded-lg border px-3 py-3 ${isDark ? "border-slate-800 bg-slate-900/40" : "border-gray-200 bg-white"}`}>
        <p className={`text-xs ${labelClass}`}>
          SF asistan: <span className={textClass}>{mr.sf_assistant_name}</span>
        </p>
      </div>
      <div className={`rounded-xl border overflow-hidden ${isDark ? "border-slate-800" : "border-gray-200"}`}>
        <p className={`px-3 py-2 text-[10px] font-semibold uppercase tracking-wide border-b ${isDark ? "border-slate-800 bg-slate-900/50 text-slate-400" : "border-gray-200 bg-gray-50 text-gray-500"}`}>
          Aşama dağılımı
        </p>
        <div className={`grid gap-px ${isDark ? "bg-slate-800" : "bg-gray-200"}`}>
          {stages.map(([stage, count]) => (
            <div key={stage} className={`flex justify-between gap-3 px-3 py-2 text-sm ${isDark ? "bg-slate-950/60" : "bg-white"}`}>
              <span className={labelClass}>{stage}</span>
              <span className={`font-medium tabular-nums ${textClass}`}>{count ?? "—"}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const PlaceholderTab = ({ title, isDark, labelClass }) => (
  <div className={`rounded-xl border border-dashed p-10 text-center ${isDark ? "border-slate-700 bg-slate-900/30" : "border-gray-200 bg-gray-50/80"}`}>
    <Sparkles className={`w-10 h-10 mx-auto mb-3 ${isDark ? "text-teal-400" : "text-teal-600"}`} />
    <h4 className={`text-sm font-semibold mb-1 ${isDark ? "text-slate-200" : "text-gray-800"}`}>{title}</h4>
    <p className={`text-xs max-w-md mx-auto ${labelClass}`}>
      Bu bölüm manuel içerik için ayrıldı. Veri kaynağı eklendiğinde burada görüntülenecek.
    </p>
  </div>
);

const SpecialAgentReportPage = ({
  apiBase,
  isDark,
  textClass,
  labelClass,
  isSuperAdmin,
  userCompanyId,
  companies = [],
  variant = "v1",
  pageTitle = "Özel Asistan Raporu",
  pageSubtitle = "Tek ekranda 6 rapor — agent ve tarih aralığına göre",
  storageKey = STORAGE_KEY,
}) => {
  const isV2 = variant === "v2";
  const panelClass = isV2
    ? cn("sar-v2 sar-v2-shell", !isDark && "sar-v2-shell--light", isDark ? "bg-slate-950/80" : "bg-slate-50")
    : (isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-gray-200");
  const inputClass = isDark ? "bg-slate-900 border-slate-700 text-slate-100" : "";
  const filterTriggerClass = cn(
    "h-10 min-h-10 w-full rounded-md border text-sm font-normal shadow-sm py-0",
    isDark ? "border-slate-700 bg-slate-900 text-slate-100" : "border-input bg-background"
  );

  const savedStateRef = useRef(typeof window !== "undefined" ? loadSavedReportState(storageKey) : null);
  const skipCompanyAgentResetRef = useRef(true);

  const [tlAudioModal, setTlAudioModal] = useState({ open: false, url: "", title: "", recordInfo: null });
  const [textDetailModal, setTextDetailModal] = useState({
    open: false,
    row: null,
    focusField: null,
    fields: [],
  });

  const [agents, setAgents] = useState([]);
  const [companyId, setCompanyId] = useState(() => {
    const saved = savedStateRef.current?.companyId;
    if (isSuperAdmin && saved) return String(saved);
    if (isSuperAdmin) return "";
    return userCompanyId ? String(userCompanyId) : "";
  });
  const [selectedAgent, setSelectedAgent] = useState(() => {
    const saved = savedStateRef.current;
    if (saved?.agentId) {
      return { id: saved.agentId, ad_soyad: saved.agentAd || saved.agentName || "" };
    }
    return null;
  });
  const [startDate, setStartDate] = useState(() => savedStateRef.current?.startDate || "");
  const [endDate, setEndDate] = useState(() => savedStateRef.current?.endDate || "");
  const [datePreset, setDatePreset] = useState(() => savedStateRef.current?.datePreset || "");
  const [activeTab, setActiveTab] = useState(() => savedStateRef.current?.activeTab || "overview");
  const [bundle, setBundle] = useState(() => savedStateRef.current?.bundle || null);
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(-1);
  const stepTimerRef = useRef(null);
  const spnDefaultAppliedRef = useRef(false);
  const excelInputRef = useRef(null);
  const [kaliteliBatchId, setKaliteliBatchId] = useState(() => savedStateRef.current?.kaliteliBatchId ?? null);
  const [importing, setImporting] = useState(false);
  const [sfSyncing, setSfSyncing] = useState(false);
  const [sfMappingApplying, setSfMappingApplying] = useState(false);
  const [sfSyncModal, setSfSyncModal] = useState({ open: false, summary: null });
  const [importModal, setImportModal] = useState({
    open: false,
    fileName: "",
    sheets: [],
    companyId: null,
    result: null,
  });

  const effectiveCompanyId = useMemo(() => {
    if (isSuperAdmin) {
      const id = parseInt(companyId, 10);
      return Number.isFinite(id) && id > 0 ? id : null;
    }
    return userCompanyId ? Number(userCompanyId) : null;
  }, [isSuperAdmin, companyId, userCompanyId]);

  const filteredAgents = useMemo(() => {
    let list = agents || [];
    if (isSuperAdmin && effectiveCompanyId) {
      list = list.filter((a) => Number(a.company_id) === effectiveCompanyId);
    }
    return agentsForSearchSelect(list);
  }, [agents, isSuperAdmin, effectiveCompanyId]);

  const importAgentOptions = useMemo(() => {
    let list = agents || [];
    const cid = importModal.companyId ?? effectiveCompanyId;
    if (cid) {
      const byCompany = list.filter((a) => Number(a.company_id) === Number(cid));
      list = byCompany.length > 0 ? byCompany : list;
    }
    return agentsForSearchSelect(list);
  }, [agents, importModal.companyId, effectiveCompanyId]);

  const kaliteColumns = useMemo(
    () => getReportColumns("kalite-degerlendirme").filter((c) => !["agent_id", "quality_user_id", "leader_user_id"].includes(c.key)),
    []
  );
  const tlYeniColumns = useMemo(
    () => getReportColumns("tl-yeni-asistan").filter((c) => !["agent_id", "leader_user_id"].includes(c.key)),
    []
  );

  const loadAgents = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/agents`);
      setAgents(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("Agent listesi yüklenemedi");
    }
  }, [apiBase]);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  useEffect(() => {
    if (!isSuperAdmin || spnDefaultAppliedRef.current) return;
    if (savedStateRef.current?.companyId) {
      spnDefaultAppliedRef.current = true;
      return;
    }
    const spnId = resolveSpnCompanyId(companies);
    if (spnId) {
      setCompanyId(spnId);
      spnDefaultAppliedRef.current = true;
    }
  }, [isSuperAdmin, companies]);

  useEffect(() => {
    if (skipCompanyAgentResetRef.current) {
      skipCompanyAgentResetRef.current = false;
      return;
    }
    setSelectedAgent(null);
  }, [effectiveCompanyId]);

  const agentLabelRestoredRef = useRef(false);
  useEffect(() => {
    if (agentLabelRestoredRef.current || !savedStateRef.current?.agentId || !agents.length) return;
    const saved = savedStateRef.current;
    const found = agents.find((a) => String(a.id) === String(saved.agentId));
    if (found) {
      setSelectedAgent({ id: found.id, ad_soyad: found.ad_soyad || found.name || saved.agentAd || "" });
      agentLabelRestoredRef.current = true;
    }
  }, [agents]);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({
        companyId: isSuperAdmin ? companyId : (userCompanyId ? String(userCompanyId) : ""),
        agentId: selectedAgent?.id ?? null,
        agentAd: selectedAgent?.ad_soyad || "",
        startDate,
        endDate,
        datePreset,
        activeTab,
        bundle,
        kaliteliBatchId,
      }));
    } catch {
      /* quota / private mode */
    }
  }, [isSuperAdmin, companyId, userCompanyId, selectedAgent, startDate, endDate, datePreset, activeTab, bundle, kaliteliBatchId, storageKey]);

  const applyDatePreset = useCallback((presetId) => {
    setDatePreset(presetId);
    if (presetId === "custom") {
      return;
    }
    const range = resolveSarDatePreset(presetId);
    if (range) {
      setStartDate(range.start);
      setEndDate(range.end);
    }
  }, []);

  const clearReportFilters = useCallback(() => {
    setDatePreset("");
    setStartDate("");
    setEndDate("");
    setBundle(null);
    setKaliteliBatchId(null);
    setLoadingStep(-1);
  }, []);

  const openTextDetail = useCallback((row, fieldKey, fields) => {
    setTextDetailModal({ open: true, row, focusField: fieldKey, fields });
  }, []);

  const closeTextDetail = useCallback(() => {
    setTextDetailModal({ open: false, row: null, focusField: null, fields: [] });
  }, []);

  const clearStepTimer = () => {
    if (stepTimerRef.current) {
      clearInterval(stepTimerRef.current);
      stepTimerRef.current = null;
    }
  };

  useEffect(() => () => clearStepTimer(), []);

  const startLoadingAnimation = () => {
    clearStepTimer();
    setLoadingStep(0);
    let step = 0;
    stepTimerRef.current = setInterval(() => {
      step = Math.min(step + 1, LOADING_STEPS.length - 2);
      setLoadingStep(step);
    }, 520);
  };

  const fetchReportBundle = useCallback(async (batchIdOverride = undefined, { silent = false } = {}) => {
    if (!selectedAgent?.id || !startDate || !endDate) return null;
    if (isSuperAdmin && !effectiveCompanyId) return null;

    const params = {
      agent_id: selectedAgent.id,
      start_date: startDate,
      end_date: endDate,
    };
    if (isSuperAdmin && effectiveCompanyId) {
      params.company_id = effectiveCompanyId;
    }
    const batchId = batchIdOverride !== undefined ? batchIdOverride : kaliteliBatchId;
    if (batchId) {
      params.kaliteli_batch_id = batchId;
    }

    const res = await axios.get(`${apiBase}/special-agent-report`, {
      params,
      timeout: 120000,
    });
    setBundle(res.data);
    if (res.data?.kaliteli_data_analiz?.selected_batch_id) {
      setKaliteliBatchId(res.data.kaliteli_data_analiz.selected_batch_id);
    }
    if (!silent) toast.success("Rapor hazır");
    return res.data;
  }, [apiBase, selectedAgent?.id, startDate, endDate, isSuperAdmin, effectiveCompanyId, kaliteliBatchId]);

  const handleQuery = async () => {
    if (!selectedAgent?.id) {
      toast.warning("Lütfen bir agent seçin");
      return;
    }
    if (!startDate || !endDate) {
      toast.warning("Başlangıç ve bitiş tarihi seçin");
      return;
    }
    if (isSuperAdmin && !effectiveCompanyId) {
      toast.warning("Lütfen firma seçin");
      return;
    }

    setKaliteliBatchId(null);
    setBundle(null);
    setLoading(true);
    startLoadingAnimation();

    try {
      await fetchReportBundle(null, { silent: false });
      clearStepTimer();
      setLoadingStep(LOADING_STEPS.length - 1);
      await new Promise((r) => setTimeout(r, 400));
    } catch (err) {
      clearStepTimer();
      setLoadingStep(-1);
      toast.error(err.response?.data?.detail || "Rapor alınamadı");
    } finally {
      setLoading(false);
    }
  };

  const handleKaliteliBatchChange = useCallback(async (batchId) => {
    setKaliteliBatchId(batchId);
    if (!bundle || !selectedAgent?.id) return;
    try {
      await fetchReportBundle(batchId, { silent: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Dönem verisi yüklenemedi");
    }
  }, [bundle, selectedAgent?.id, fetchReportBundle]);

  const handleExcelFilePick = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (isSuperAdmin && !effectiveCompanyId) {
      toast.warning("Import için firma seçin");
      return;
    }
    const companyForImport = isSuperAdmin ? effectiveCompanyId : Number(userCompanyId);
    if (!companyForImport) {
      toast.warning("Firma bilgisi eksik");
      return;
    }

    try {
      const buffer = await file.arrayBuffer();
      if (!agents.length) {
        await loadAgents();
      }
      const sheets = parseKaliteliDataWorkbook(buffer);
      if (!sheets.length) {
        toast.error("Excel içinde uygun sheet bulunamadı");
        return;
      }
      setImportModal({
        open: true,
        fileName: file.name,
        sheets,
        companyId: companyForImport,
        result: null,
      });
    } catch {
      toast.error("Excel dosyası okunamadı");
    }
  };

  const closeImportModal = () => {
    setImportModal({
      open: false,
      fileName: "",
      sheets: [],
      companyId: null,
      result: null,
    });
  };

  const handleSfDatamSync = async () => {
    if (!startDate || !endDate) {
      toast.warning("SF için başlangıç ve bitiş tarihi seçin");
      return;
    }
    const companyForSync = isSuperAdmin ? effectiveCompanyId : Number(userCompanyId);
    if (!companyForSync) {
      toast.warning(isSuperAdmin ? "Firma seçin" : "Firma bilgisi eksik");
      return;
    }

    setSfSyncing(true);
    try {
      const res = await axios.post(`${apiBase}/special-agent-sf-datam-sync`, {
        company_id: companyForSync,
        start_date: startDate,
        end_date: endDate,
      }, { timeout: 180000 });
      const summary = res.data?.summary || {};
      const unmatchedN = summary.unmatched_count ?? 0;
      setSfSyncModal({ open: false, summary });
      if (unmatchedN > 0) {
        toast.success(
          `SF: ${summary.matched_count ?? 0} asistan kaydedildi. ${unmatchedN} isim atlandı (rapora dahil değil). İsterseniz «SF eşleştir» ile eşleyebilirsiniz.`,
          { duration: 7000 },
        );
      } else {
        toast.success(`SF: ${summary.matched_count ?? 0} asistan kaydedildi.`);
      }
      if (selectedAgent?.id && startDate && endDate) {
        await fetchReportBundle(undefined, { silent: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Salesforce senkron başarısız");
    } finally {
      setSfSyncing(false);
    }
  };

  const handleApplySfMappings = async (mappings) => {
    const summary = sfSyncModal.summary;
    const companyForSync = isSuperAdmin ? effectiveCompanyId : Number(userCompanyId);
    if (!summary?.batch_id || !companyForSync || !mappings?.length) return;

    setSfMappingApplying(true);
    try {
      const res = await axios.post(`${apiBase}/special-agent-sf-datam-sync`, {
        action: "apply_mappings",
        company_id: companyForSync,
        batch_id: summary.batch_id,
        mappings,
      }, { timeout: 120000 });
      const next = res.data?.summary || {};
      setSfSyncModal((prev) => ({
        ...prev,
        summary: {
          ...prev.summary,
          ...next,
          matched_count: next.matched_count,
          unmatched_count: next.unmatched_count,
          unmatched: next.unmatched ?? [],
        },
      }));
      toast.success(`${next.applied ?? mappings.length} manuel eşleştirme kaydedildi`);
      if ((next.unmatched_count ?? 0) === 0) {
        setSfSyncModal((prev) => ({ ...prev, open: false }));
      }
      if (selectedAgent?.id && startDate && endDate) {
        await fetchReportBundle(undefined, { silent: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eşleştirme kaydedilemedi");
    } finally {
      setSfMappingApplying(false);
    }
  };

  const handleImportConfirm = async ({ sheets, agent_name_map, only_matched_rows }) => {
    const companyForImport = importModal.companyId;
    if (!companyForImport || !sheets?.length) return;

    setImporting(true);
    try {
      const res = await axios.post(`${apiBase}/special-agent-kaliteli-data`, {
        company_id: companyForImport,
        sheets,
        agent_name_map,
        only_matched_rows: only_matched_rows !== false,
      }, { timeout: 120000 });
      const summary = res.data?.summary || {};
      setImportModal((prev) => ({ ...prev, result: summary }));
      const parts = [];
      if ((summary.inserted ?? 0) > 0) parts.push(`${summary.inserted} yeni satır`);
      if ((summary.updated ?? 0) > 0) parts.push(`${summary.updated} güncellendi`);
      if ((summary.unchanged ?? 0) > 0) parts.push(`${summary.unchanged} aynı (tekrar eklenmedi)`);
      if ((summary.deleted ?? 0) > 0) parts.push(`${summary.deleted} kaldırıldı`);
      if (parts.length) {
        toast.success(`Kaliteli Data import: ${parts.join(", ")}`);
      } else if ((summary.skipped ?? 0) > 0) {
        toast.warning("Eşleşen satır import edilmedi; isimleri kontrol edin");
      } else {
        toast.success("Kaliteli Data import tamamlandı");
      }
      if (
        selectedAgent?.id
        && startDate
        && endDate
        && (!isSuperAdmin || effectiveCompanyId)
      ) {
        await fetchReportBundle(null, { silent: true });
      } else if ((summary.inserted ?? 0) + (summary.updated ?? 0) + (summary.unchanged ?? 0) > 0) {
        toast.info("Veriyi görmek için agent ve tarih aralığı seçip Sorgula'ya basın.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Excel import başarısız");
    } finally {
      setImporting(false);
    }
  };

  const formatTlCell = useCallback((col, row) => {
    const value = row?.[col.key];
    if (col.type === "date") return formatReportDate(value);
    if (col.type === "approval") return formatOnayLabel(value) || "—";
    if (col.type === "url") {
      return (
        <TlRecordingUrlActions
          url={value}
          apiBase={apiBase}
          isDark={isDark}
          labelClass={labelClass}
          onPlay={(recordingUrl) => {
            const title = `${col.label}${row?.assistant_name ? ` · ${row.assistant_name}` : ""}`;
            setTlAudioModal({
              open: true,
              url: recordingUrl,
              title,
              recordInfo: buildReportRecordingInfo({
                title,
                url: recordingUrl,
                reportRowId: row?.id,
                agentName: row?.assistant_name,
                sourceLabel: "Özel Asistan Raporu",
              }),
            });
          }}
        />
      );
    }
    if (col.type === "text") {
      return renderClickableReportText(value, row, col.key, (r, key) => openTextDetail(r, key, TL_TEXT_FIELDS), isDark, labelClass);
    }
    return value ?? "—";
  }, [apiBase, isDark, labelClass, openTextDetail]);

  const tabCounts = useMemo(() => {
    if (!bundle) return {};
    return {
      tl: bundle.tl_recordings?.total ?? 0,
      kalite: bundle.kalite_degerlendirme?.total ?? 0,
      mola: bundle.mola_report?.summary?.active_days ?? bundle.mola_report?.daily_breakdown?.length ?? 0,
      tl_yeni: bundle.tl_yeni_asistan?.total ?? 0,
      kaliteli: bundle.kaliteli_data_analiz?.row ? 1 : 0,
      mr: bundle.toplam_data_mr?.has_agent_row ? 1 : 0,
    };
  }, [bundle]);

  const renderTabContent = () => {
    if (!bundle) return null;

    switch (activeTab) {
      case "overview":
        return (
          <SpecialAgentReportOverview
            apiBase={apiBase}
            bundle={bundle}
            isDark={isDark}
            textClass={textClass}
            labelClass={labelClass}
            onOpenTab={setActiveTab}
            onKaliteliBatchChange={handleKaliteliBatchChange}
            variant={variant}
          />
        );
      case "tl":
        return (
          <div className={`overflow-x-auto rounded-lg border ${isDark ? "border-slate-800" : "border-gray-200"}`}>
            <table className="w-full text-sm">
              <TableHeader>
                <PanelTableHeadRow>
                  {TL_TABLE_COLUMNS.map((col) => (
                    <PanelTableHead key={col.key} className="text-xs whitespace-nowrap">{col.label}</PanelTableHead>
                  ))}
                </PanelTableHeadRow>
              </TableHeader>
              <PanelTableBody>
                <ReadOnlyReportTable
                  columns={TL_TABLE_COLUMNS}
                  rows={bundle.tl_recordings?.items}
                  formatCell={formatTlCell}
                  isDark={isDark}
                  emptyLabel="Seçilen aralıkta ses kaydı raporu yok"
                />
              </PanelTableBody>
            </table>
            {(bundle.tl_recordings?.total || 0) > (bundle.tl_recordings?.items?.length || 0) && (
              <p className={`text-[11px] px-3 py-2 ${labelClass}`}>
                İlk {bundle.tl_recordings.items.length} / {bundle.tl_recordings.total} kayıt gösteriliyor
              </p>
            )}
          </div>
        );
      case "kalite":
        return (
          <div className={`overflow-x-auto rounded-lg border ${isDark ? "border-slate-800" : "border-gray-200"}`}>
            <table className="w-full text-sm">
              <TableHeader>
                <PanelTableHeadRow>
                  {kaliteColumns.map((col) => (
                    <PanelTableHead key={col.key} className="text-xs whitespace-nowrap">{col.label}</PanelTableHead>
                  ))}
                </PanelTableHeadRow>
              </TableHeader>
              <PanelTableBody>
                <ReadOnlyReportTable
                  columns={kaliteColumns}
                  rows={bundle.kalite_degerlendirme?.items}
                  formatCell={(col, row) => formatMgrCell("kalite-degerlendirme", col, row, (r, key) => openTextDetail(r, key, KALITE_TEXT_FIELDS), isDark, labelClass)}
                  isDark={isDark}
                  emptyLabel="Kalite değerlendirme kaydı yok"
                />
              </PanelTableBody>
            </table>
          </div>
        );
      case "mola":
        return (
          <BreakAnalysisRangeOverviewPanel
            agentName={bundle.agent?.ad_soyad}
            period={bundle.mola_report?.period || { start: bundle.start_date, end: bundle.end_date }}
            days={bundle.mola_report?.daily_breakdown}
            summary={bundle.mola_report?.summary}
            isDark={isDark}
            textClass={textClass}
            labelClass={labelClass}
          />
        );
      case "tl_yeni":
        return (
          <div className={`overflow-x-auto rounded-lg border ${isDark ? "border-slate-800" : "border-gray-200"}`}>
            <table className="w-full text-sm">
              <TableHeader>
                <PanelTableHeadRow>
                  {tlYeniColumns.map((col) => (
                    <PanelTableHead key={col.key} className="text-xs whitespace-nowrap">{col.label}</PanelTableHead>
                  ))}
                </PanelTableHeadRow>
              </TableHeader>
              <PanelTableBody>
                <ReadOnlyReportTable
                  columns={tlYeniColumns}
                  rows={bundle.tl_yeni_asistan?.items}
                  formatCell={(col, row) => formatMgrCell("tl-yeni-asistan", col, row, (r, key) => openTextDetail(r, key, TL_YENI_TEXT_FIELDS), isDark, labelClass)}
                  isDark={isDark}
                  emptyLabel="Yeni asistan raporu kaydı yok"
                />
              </PanelTableBody>
            </table>
          </div>
        );
      case "kaliteli":
        return (
          <KaliteliDataDetailPanel
            bundle={bundle}
            isDark={isDark}
            textClass={textClass}
            labelClass={labelClass}
            kaliteliBatchId={kaliteliBatchId}
            onBatchChange={setKaliteliBatchId}
            onRefetch={handleKaliteliBatchChange}
          />
        );
      case "mr":
        return (
          <SfDatamMrPanel
            bundle={bundle}
            isDark={isDark}
            textClass={textClass}
            labelClass={labelClass}
            variant={variant}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={cn("rounded-2xl overflow-hidden", !isV2 && "border", panelClass)}>
      <div
        className={cn(
          "px-4 py-4 border-b",
          isV2
            ? "sar-v2-hero"
            : (isDark ? "border-slate-800 bg-gradient-to-r from-teal-950/40 via-slate-900 to-cyan-950/30" : "border-gray-100 bg-gradient-to-r from-teal-50 to-white")
        )}
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                "p-2.5 rounded-xl",
                isV2 ? "sar-v2-hero-icon bg-teal-500/15 text-teal-400" : `sar-loading-pulse ${isDark ? "bg-teal-500/15 text-teal-400" : "bg-teal-50 text-teal-600"}`
              )}
            >
              <Sparkles className="w-6 h-6" />
            </div>
            <div>
              {isV2 && <span className="sar-v2-badge inline-block mb-1.5">Pro analitik</span>}
              <h3 className={`text-lg font-semibold ${textClass}`}>{pageTitle}</h3>
              <p className={`text-xs ${labelClass}`}>{pageSubtitle}</p>
            </div>
          </div>
          {bundle?.agent && (
            <div
              className={cn(
                "text-xs rounded-lg px-3 py-2 max-w-md",
                isV2 ? "sar-v2-agent-chip text-slate-300" : (isDark ? "bg-slate-800/80 text-slate-300" : "bg-white border border-gray-100 text-gray-600")
              )}
            >
              <p className={`text-sm font-semibold ${textClass}`}>{bundle.agent.ad_soyad}</p>
              <p className={`mt-1 leading-relaxed ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                {bundle.agent.team_leader_name ? (
                  <>
                    <span className={labelClass}>Takım Lideri:</span>
                    {" "}
                    <span className="font-medium">{bundle.agent.team_leader_name}</span>
                  </>
                ) : (
                  <span className={labelClass}>Takım Lideri: —</span>
                )}
                {bundle.agent.work_duration ? (
                  <>
                    {" · "}
                    <span className={labelClass}>Personel Çalışma Süresi:</span>
                    {" "}
                    <span className="font-medium">{bundle.agent.work_duration}</span>
                  </>
                ) : (
                  <>
                    {" · "}
                    <span className={labelClass}>Personel Çalışma Süresi: —</span>
                  </>
                )}
              </p>
              <p className={`mt-1 ${labelClass}`}>
                {formatReportDate(bundle.start_date)} – {formatReportDate(bundle.end_date)}
              </p>
            </div>
          )}
        </div>
      </div>

      <div
        className={cn(
          "px-4 py-2.5 border-b",
          isV2 ? "sar-v2-filter" : (isDark ? "border-slate-800 bg-slate-950/30" : "border-gray-100 bg-gray-50/80")
        )}
      >
        <div className="sar-filter-bar">
          {isSuperAdmin && (
            <div className="w-full sm:w-[140px] xl:w-[132px] shrink-0">
              <Select value={companyId || ""} onValueChange={setCompanyId}>
                <SelectTrigger className={cn(filterTriggerClass, "h-9 min-h-9 gap-1.5 text-xs", inputClass)}>
                  <Building2 className="h-3.5 w-3.5 shrink-0 opacity-60" />
                  <SelectValue placeholder="Firma" />
                </SelectTrigger>
                <SelectContent>
                  {(companies || []).map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.ad || c.name || `Firma #${c.id}`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="w-full sm:w-[min(100%,200px)] xl:w-[188px] shrink-0">
            <SearchableAgentSelect
              agents={filteredAgents}
              value={selectedAgent}
              onChange={setSelectedAgent}
              placeholder={isSuperAdmin && !effectiveCompanyId ? "Firma seç…" : "Asistan…"}
              isDark={isDark}
              inputClass={cn(filterTriggerClass, "h-9 min-h-9 text-xs", inputClass)}
              labelClass={labelClass}
              size="default"
              leftIcon={User}
              nameOnly
            />
          </div>

          <div className="sar-filter-pills">
            {SAR_DATE_PRESETS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => applyDatePreset(opt.id)}
                className={`sar-filter-pill ${
                  datePreset === opt.id
                    ? (isDark ? "bg-teal-600 text-white" : "bg-teal-600 text-white")
                    : (isDark ? "bg-slate-800 text-slate-400 hover:bg-slate-700" : "bg-gray-100 text-gray-600 hover:bg-gray-200")
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <Input
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setDatePreset("custom");
            }}
            className={cn(filterTriggerClass, "h-9 min-h-9 w-[128px] shrink-0 text-xs", inputClass)}
            title="Başlangıç"
          />
          <Input
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setDatePreset("custom");
            }}
            className={cn(filterTriggerClass, "h-9 min-h-9 w-[128px] shrink-0 text-xs", inputClass)}
            title="Bitiş"
          />

          <Button
            type="button"
            variant="outline"
            size="icon"
            className={cn("h-9 w-9 shrink-0", isDark ? "border-slate-700" : "")}
            onClick={clearReportFilters}
            title="Filtreleri temizle"
          >
            <RotateCcw className="w-4 h-4" />
          </Button>

          <Button
            type="button"
            size="sm"
            className="h-9 shrink-0 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500 px-3"
            disabled={loading}
            onClick={handleQuery}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            <span className="ml-1.5 hidden 2xl:inline">Sorgula</span>
          </Button>

          <input
            ref={excelInputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={handleExcelFilePick}
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            className={cn("h-9 w-9 shrink-0", isDark ? "border-slate-700" : "")}
            disabled={loading || importing}
            title="Excel import (Kaliteli Data)"
            onClick={() => excelInputRef.current?.click()}
          >
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className={cn("h-9 w-9 shrink-0", isDark ? "border-sky-700 text-sky-600" : "border-sky-300 text-sky-700")}
            disabled={loading || sfSyncing || importing}
            title="Salesforce Datam senkron"
            onClick={handleSfDatamSync}
          >
            {sfSyncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CloudDownload className="w-4 h-4" />}
          </Button>
          {(sfSyncModal.summary?.unmatched_count ?? 0) > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={cn("h-9 shrink-0 text-xs px-2", isDark ? "border-amber-700/60 text-amber-200" : "border-amber-300 text-amber-900")}
              disabled={loading || sfSyncing || sfMappingApplying}
              title="Eşleşmeyen SF isimleri"
              onClick={() => setSfSyncModal((prev) => ({ ...prev, open: true }))}
            >
              SF ({sfSyncModal.summary.unmatched_count})
            </Button>
          )}
        </div>
      </div>

      <TlRecordingAudioModal
        open={tlAudioModal.open}
        onClose={() => setTlAudioModal({ open: false, url: "", title: "", recordInfo: null })}
        url={tlAudioModal.url}
        title={tlAudioModal.title}
        isDark={isDark}
        labelClass={labelClass}
        apiBase={apiBase}
        recordInfo={tlAudioModal.recordInfo}
      />

      <ReportTextDetailModal
        open={textDetailModal.open}
        onClose={closeTextDetail}
        row={textDetailModal.row}
        focusField={textDetailModal.focusField}
        fields={textDetailModal.fields}
        isDark={isDark}
        textClass={textClass}
        labelClass={labelClass}
      />

      <SfDatamSyncResultModal
        open={sfSyncModal.open}
        onClose={() => setSfSyncModal({ open: false, summary: null })}
        summary={sfSyncModal.summary}
        agents={importAgentOptions}
        applying={sfMappingApplying}
        onApplyMappings={handleApplySfMappings}
        isDark={isDark}
        textClass={textClass}
        labelClass={labelClass}
      />

      <KaliteliDataImportModal
        open={importModal.open}
        onClose={closeImportModal}
        fileName={importModal.fileName}
        sheets={importModal.sheets}
        agents={importAgentOptions}
        isDark={isDark}
        textClass={textClass}
        labelClass={labelClass}
        importing={importing}
        importResult={importModal.result}
        onConfirm={handleImportConfirm}
      />

      {loading && (
        <div
          className={cn(
            "px-4 py-8 border-b",
            isV2 ? "sar-v2-loading" : (isDark ? "border-slate-800 bg-slate-950/50" : "border-gray-100 bg-slate-50")
          )}
        >
          <div className="max-w-lg mx-auto text-center">
            <div className="relative w-16 h-16 mx-auto mb-4">
              <div className={`absolute inset-0 rounded-full border-2 border-dashed sar-loading-orbit ${isDark ? "border-teal-500/40" : "border-teal-300"}`} />
              <div className={`absolute inset-2 rounded-full flex items-center justify-center ${isDark ? "bg-teal-500/20" : "bg-teal-100"}`}>
                <CalendarRange className={`w-6 h-6 ${isDark ? "text-teal-400" : "text-teal-600"}`} />
              </div>
            </div>
            <p className={`text-sm font-medium mb-4 ${textClass}`}>Raporlar hazırlanıyor…</p>
            <div className="h-1.5 rounded-full overflow-hidden mb-4 sar-shimmer-bar" />
            <ul className="space-y-2 text-left">
              {LOADING_STEPS.map((step, idx) => {
                const done = loadingStep > idx;
                const active = loadingStep === idx;
                return (
                  <li
                    key={step.key}
                    className={`flex items-center gap-3 text-xs transition-opacity ${idx > loadingStep + 1 ? "opacity-30" : "opacity-100"} ${active ? "sar-step-active" : ""} ${done ? "sar-step-done" : ""}`}
                  >
                    <span className={`sar-step-dot w-2.5 h-2.5 rounded-full shrink-0 transition-transform ${done || active ? (isDark ? "bg-teal-400" : "bg-teal-500") : (isDark ? "bg-slate-600" : "bg-gray-300")}`} />
                    <span className={active ? (isDark ? "text-teal-200 font-medium" : "text-teal-800 font-medium") : labelClass}>{step.label}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {bundle && !loading && (
        <>
          <div
            className={cn(
              "flex flex-wrap gap-1 px-3 py-2 border-b",
              isV2 ? "sar-v2-tabs" : (isDark ? "border-slate-800 bg-slate-900/40" : "border-gray-100 bg-white")
            )}
          >
            {TAB_DEFS.map((tab) => {
              const Icon = tab.icon;
              const count = tabCounts[tab.id];
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  data-active={isV2 ? isActive : undefined}
                  className={cn(
                    "inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors",
                    isV2 && "sar-v2-tab",
                    isActive
                      ? (isV2
                        ? (isDark ? "text-teal-50 font-semibold" : "text-teal-900 font-semibold")
                        : (isDark ? "bg-teal-500/20 text-teal-200 ring-1 ring-teal-500/30" : "bg-teal-50 text-teal-800 ring-1 ring-teal-200"))
                      : (isDark ? "text-slate-400 hover:bg-slate-800" : "text-gray-600 hover:bg-gray-50")
                  )}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {tab.label}
                  {tab.id !== "overview" && count != null && count > 0 && (
                    <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] ${isDark ? "bg-slate-700" : "bg-gray-200"}`}>{count}</span>
                  )}
                </button>
              );
            })}
          </div>
          <div className={cn("p-4", isV2 ? "sar-v2-tab-content" : "sar-tab-enter")}>{renderTabContent()}</div>
        </>
      )}

      {!bundle && !loading && (
        <div className={cn("px-6 py-16 text-center", labelClass, isV2 && "sar-v2-empty")}>
          <Sparkles
            className={cn(
              "w-12 h-12 mx-auto mb-3 opacity-40",
              isDark ? "text-teal-400" : "text-teal-600",
              isV2 && "sar-v2-empty-icon opacity-100"
            )}
          />
          <p className="text-sm">
            {isV2
              ? "Asistan ve dönem seçin — ardından "
              : "Agent ve tarih aralığı seçip "}
            <strong className="font-medium">Sorgula</strong>
            {isV2 ? " ile modülleri yükleyin." : " ile raporları getirin."}
          </p>
        </div>
      )}
    </div>
  );
};

export default SpecialAgentReportPage;
